import { HashConnect } from 'hashconnect';
import {
  LedgerId,
  ContractExecuteTransaction,
  ContractId,
  Hbar,
  AccountId,
  TransactionId
} from '@hashgraph/sdk';

interface HederaAccount {
  accountId: string;
  accountBalance: string;
}

interface WalletState {
  isConnected: boolean;
  accounts: HederaAccount[];
  selectedAccount?: HederaAccount;
  network: 'testnet' | 'mainnet';
}

const HEDERA_NETWORKS = {
  mainnet: {
    name: 'Hedera Mainnet',
    nodeUrl: 'https://mainnet-public.mirrornode.hedera.com',
    mirrorNodeUrl: 'https://mainnet-public.mirrornode.hedera.com',
    chainId: '295'
  },
  testnet: {
    name: 'Hedera Testnet',
    nodeUrl: 'https://testnet.mirrornode.hedera.com',
    mirrorNodeUrl: 'https://testnet.mirrornode.hedera.com',
    chainId: '296'
  }
};

const HASHPACK_METADATA = {
  name: 'COPYCAT Swap',
  description: 'Profitable SaucerSwap with Lottery Integration',
  url: window.location.origin,
  icon: window.location.origin + '/favicon.ico',
  icons: [window.location.origin + '/favicon.ico']
};

// WalletConnect Project ID - Get from https://cloud.walletconnect.com/
const WALLETCONNECT_PROJECT_ID = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || '845fb350ba83d50180234b4f77a4455a';

class HashPackService {
  private network: 'testnet' | 'mainnet' = 'testnet';
  private accounts: HederaAccount[] = [];
  private hashConnect: HashConnect;
  private initPromise: Promise<void>;
  private eventListeners: Array<(state: WalletState) => void> = [];

  constructor() {
    // Initialize HashConnect with the official pattern
    this.hashConnect = new HashConnect(
      LedgerId.fromString(this.network),
      WALLETCONNECT_PROJECT_ID,
      HASHPACK_METADATA,
      true // debug mode
    );

    this.initPromise = this.initialize();
    this.setupEventListeners();
  }

  private async initialize(): Promise<void> {
    try {
      console.log('Initializing HashConnect...');

      // Simple initialization like the official example
      await this.hashConnect.init();
      console.log('HashConnect initialized successfully');

      // Update accounts when connection changes
      this.syncAccounts();
    } catch (error) {
      console.error('Failed to initialize HashConnect:', error);
      throw error;
    }
  }

  private setupEventListeners(): void {
    // Listen for pairing events - wallet connected
    this.hashConnect.pairingEvent.on(() => {
      console.log('✅ Wallet paired successfully');
      this.syncAccounts();
      this.notifyListeners();
    });

    // Listen for disconnection events
    this.hashConnect.disconnectionEvent.on(() => {
      console.log('❌ Wallet disconnected');
      this.syncAccounts();
      this.notifyListeners();
    });

    // Listen for connection status changes
    this.hashConnect.connectionStatusChangeEvent.on(() => {
      console.log('📡 Connection status changed');
      this.syncAccounts();
      this.notifyListeners();
    });
  }

  private syncAccounts(): void {
    const connectedAccountIds = this.hashConnect.connectedAccountIds;
    console.log('Connected account IDs:', connectedAccountIds);

    if (connectedAccountIds && connectedAccountIds.length > 0) {
      this.accounts = connectedAccountIds.map((accountId: any) => ({
        accountId: accountId.toString(),
        accountBalance: '0 HBAR'
      }));
      console.log('✅ Wallet connected with accounts:', this.accounts);
      this.updateAccountBalances();
    } else {
      this.accounts = [];
      console.log('❌ No connected accounts');
    }
  }

  private async updateAccountBalances(): Promise<void> {
    try {
      for (let i = 0; i < this.accounts.length; i++) {
        const account = this.accounts[i];
        const balance = await this.getAccountBalance(account.accountId);
        this.accounts[i].accountBalance = balance;
      }
      this.notifyListeners();
    } catch (error) {
      console.error('Failed to update account balances:', error);
    }
  }

  private async getAccountBalance(accountId: string): Promise<string> {
    try {
      const networkConfig = HEDERA_NETWORKS[this.network];
      const response = await fetch(`${networkConfig.mirrorNodeUrl}/api/v1/accounts/${accountId}`);
      const data = await response.json();

      if (data.balance) {
        const hbarBalance = (data.balance.balance / 100000000).toFixed(2);
        return `${hbarBalance} HBAR`;
      }
      return '0 HBAR';
    } catch (error) {
      console.error('Failed to fetch balance:', error);
      return '0 HBAR';
    }
  }

  private notifyListeners(): void {
    const state = this.getWalletState();
    this.eventListeners.forEach(listener => listener(state));
  }

  async connectWallet(): Promise<HederaAccount[]> {
    try {
      console.log('🔗 Opening HashConnect pairing modal...');

      // Wait for initialization to complete
      await this.initPromise;

      // If already connected, return existing accounts
      if (this.accounts.length > 0) {
        console.log('✅ Already connected, returning existing accounts');
        return this.accounts;
      }

      // Use the official method to open pairing modal
      this.hashConnect.openPairingModal();

      // Create a promise that resolves when pairing completes
      const pairingPromise = new Promise<HederaAccount[]>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Connection timeout after 60 seconds'));
        }, 60000);

        const checkConnection = () => {
          if (this.accounts.length > 0) {
            clearTimeout(timeout);
            resolve(this.accounts);
          } else {
            setTimeout(checkConnection, 1000);
          }
        };

        checkConnection();
      });

      return await pairingPromise;
    } catch (error) {
      console.error('❌ Wallet connection failed:', error);
      throw error;
    }
  }

  async disconnectWallet(): Promise<void> {
    try {
      await this.initPromise;
      this.hashConnect.disconnect();
      this.accounts = [];
      console.log('✅ Wallet disconnected');
      this.notifyListeners();
    } catch (error) {
      console.error('Wallet disconnection failed:', error);
      throw error;
    }
  }

  getWalletState(): WalletState {
    return {
      isConnected: this.accounts.length > 0,
      accounts: this.accounts,
      selectedAccount: this.accounts[0],
      network: this.network
    };
  }

  isWalletConnected(): boolean {
    return this.accounts.length > 0;
  }

  getConnectedAccountIds(): string[] {
    return this.accounts.map(account => account.accountId);
  }

  getPrimaryAccountId(): string | null {
    return this.accounts.length > 0 ? this.accounts[0].accountId : null;
  }

  // Event listener management
  onStateChange(callback: (state: WalletState) => void): () => void {
    this.eventListeners.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.eventListeners.indexOf(callback);
      if (index > -1) {
        this.eventListeners.splice(index, 1);
      }
    };
  }

  // Contract execution methods using real HashConnect
  async executeContractFunction(
    contractId: string,
    functionName: string,
    parameters: any,
    gasLimit: number
  ): Promise<any> {
    try {
      if (this.accounts.length === 0) {
        throw new Error('HashPack wallet not connected');
      }

      console.log('🔄 Executing REAL contract function:', {
        contractId,
        functionName,
        gasLimit,
        parameters: parameters ? 'provided' : 'none',
        timestamp: new Date().toISOString()
      });

      // Build the contract execute transaction
      let transaction = new ContractExecuteTransaction()
        .setContractId(ContractId.fromString(contractId))
        .setGas(gasLimit);

      // Add function parameters if provided
      if (parameters && typeof parameters.buildHAPIParams === 'function') {
        transaction = transaction.setFunction(functionName, parameters.buildHAPIParams());
      } else {
        transaction = transaction.setFunction(functionName);
      }

      // For payable functions (like HBAR swaps), set the payable amount
      if (functionName.includes('HBAR') && parameters?.payableAmount) {
        transaction = transaction.setPayableAmount(Hbar.fromTinybars(parameters.payableAmount));
      }

      console.log('📄 Built transaction:', transaction);

      // Set consensus node account IDs based on network
      const nodeAccountIds = this.network === 'testnet'
        ? [
            AccountId.fromString("0.0.3"),   // Hedera consensus node 0
            AccountId.fromString("0.0.4"),   // Hedera consensus node 1
            AccountId.fromString("0.0.5")    // Hedera consensus node 2
          ]
        : [
            AccountId.fromString("0.0.3"),   // Hedera mainnet consensus node 0
            AccountId.fromString("0.0.4"),   // Hedera mainnet consensus node 1
            AccountId.fromString("0.0.5")    // Hedera mainnet consensus node 2
          ];

      transaction = transaction.setNodeAccountIds(nodeAccountIds);

      // Set transaction ID
      const transactionId = TransactionId.generate(AccountId.fromString(this.accounts[0].accountId));
      transaction = transaction.setTransactionId(transactionId);

      // Freeze the transaction
      const frozenTransaction = transaction.freeze();

      // Convert to bytes
      const transactionBytes = frozenTransaction.toBytes();

      console.log('📡 Sending transaction to HashPack for signing...');

      // Send transaction through HashConnect using the new API
      // Use the connected account ID from HashConnect's own SDK
      const connectedAccountIds = this.hashConnect.connectedAccountIds;
      if (connectedAccountIds.length === 0) {
        throw new Error('No connected accounts found in HashConnect');
      }

      const response = await this.hashConnect.sendTransaction(connectedAccountIds[0], frozenTransaction as any);

      console.log('✅ REAL contract function executed:', response);

      // Return the transaction ID from the TransactionReceipt
      return response.toJSON();
    } catch (error) {
      console.error('❌ REAL contract function execution failed:', error);
      throw error;
    }
  }
}

export const hashPackService = new HashPackService();